export enum UserRole {
  SUPERADMIN = 'super_admin',
  ADMIN = 'admin',
  USER = 'user'
}

export enum CompanyPlan {
  STARTER = 'starter',
  BUSINESS = 'business',
  ENTERPRISE = 'enterprise'
}

export enum StripeSubscriptionStatus {
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid'
}

export enum CalendarProvider {
  GOOGLE = 'google',
  OUTLOOK = 'outlook'
}

export enum SessionType {
  Workweek = 'workweek',
  Fullweek = 'fullweek',
  Individual = 'individual'
}

export enum MeetingType {
  Remote = 'remote',
  Onsite = 'onsite'
}

export enum Weekday {
  Mon = 'mon',
  Tue = 'tue',
  Wed = 'wed',
  Thu = 'thu',
  Fri = 'fri',
  Sat = 'sat',
  Sun = 'sun'
}

export enum AppointmentStatus {
  WaitingForProposer = 'waiting_for_proposer',
  WaitingForSelector = 'waiting_for_selector',
  Confirmed = 'confirmed',
  Rejected = 'rejected'
}

export enum AppointmentSlotStatus {
  Selected = 'selected',
  Confirmed = 'confirmed',
  Rejected = 'rejected'
}

export enum ActivityAction {
  SessionStarted = 'session_started',
  SessionOpened = 'session_opened',
  SessionDeleted = 'session_deleted',
  SessionStatusChanged = 'session_status_changed',
  ProposalsSubmitted = 'proposals_submitted',
  ProposalsReplaced = 'proposals_replaced',
  AppointmentConfirmed = 'appointment_confirmed',
  AppointmentRejected = 'appointment_rejected',
  AppointmentSynced = 'appointment_synced',
  NotificationSent = 'notification_sent',
  LinkResent = 'link_resent',
  ApplicationReviewed = 'application.reviewed',
  ApplicationRejected = 'application.rejected',
  ApplicationConvertedToSession = 'application.converted_to_session'
}

export enum ActivityEntityType {
  Session = 'session',
  Appointment = 'appointment',
  Application = 'application'
}

export enum AddonType {
  Recruiting = 'recruiting'
}

export enum EmploymentType {
  FullTime = 'full_time',
  PartTime = 'part_time',
  Contract = 'contract',
  Temporary = 'temporary',
  Internship = 'internship',
  Freelance = 'freelance'
}

export enum JobAdvertisementStatus {
  Draft = 'draft',
  Active = 'active',
  Archived = 'archived'
}

export enum Gender {
  Male = 'male',
  Female = 'female',
  NonBinary = 'non_binary'
}

export enum CandidateStatus {
  Sourced = 'sourced',
  Applied = 'applied',
  Screening = 'screening',
  Interview = 'interview',
  Offered = 'offered',
  Hired = 'hired',
  Rejected = 'rejected',
  Withdrawn = 'withdrawn'
}

export enum JobSessionStatus {
  Open = 'open',
  Closed = 'closed'
}

export enum CandidateInterest {
  Interested = 'interested',
  NotInterested = 'not_interested'
}

export enum PartnerInterest {
  Interested = 'interested',
  NotInterested = 'not_interested',
  Pending = 'pending'
}

export enum ActorType {
  Recruiter = 'recruiter',
  Candidate = 'candidate',
  Partner = 'partner',
  System = 'system'
}

export enum JobSessionLogAction {
  SessionCreated = 'session.created',
  SessionShared = 'session.shared_to_candidate',
  SessionClosed = 'session.closed',
  AdvertisementAdded = 'advertisement.added',
  AdvertisementShared = 'advertisement.shared_to_partner',
  AdvertisementRemoved = 'advertisement.removed',
  CandidateInterestExpressed = 'candidate.interest.expressed',
  CandidateInterestWithdrawn = 'candidate.interest.withdrawn',
  PartnerInterestExpressed = 'partner.interest.expressed',
  PartnerInterestWithdrawn = 'partner.interest.withdrawn',
  PartnerViewed = 'partner.viewed',
  NotesUpdated = 'notes.updated'
}

export enum CandidateSource {
  HeyflowSocial = 'heyflow_social',
  LinkedIn = 'linkedin',
  JobsCh = 'jobs_ch',
  Manual = 'manual',
  Referral = 'referral'
}

export enum JobApplicationSource {
  HeyflowSocial = 'heyflow_social',
  LinkedIn = 'linkedin',
  JobsCh = 'jobs_ch',
  Platform = 'platform',
  Other = 'other'
}

export enum JobApplicationStatus {
  PendingReview = 'pending_review',
  Approved = 'approved',
  Rejected = 'rejected'
}

export interface HeyflowFlow {
  id: string;
  name: string;
  webhook_secret?: string | null;
  domain?: string | null;
}

export interface RecruitingSettings {
  id: string;
  user_id: string;
  heyflow_flows: HeyflowFlow[];
  created_at: string;
  updated_at: string;
}

export interface AddonSubscription {
  id: string;
  company_id: string;
  addon_type: AddonType;
  stripe_subscription_id?: string | null;
  stripe_price_id?: string | null;
  period_ends_at?: string | null;
  cancel_at_period_end: boolean;
  stripe_subscription_status?: StripeSubscriptionStatus | null;
  created_at: string;
  updated_at: string;
}

export interface JobAdvertisement {
  id: string;
  company_id: string;
  partner_id: string;
  title: string;
  description?: string | null;
  employment_type: EmploymentType;
  location?: string | null;
  workload_min?: number | null;
  workload_max?: number | null;
  languages?: string | null;
  salary_range_min?: number | null;
  salary_range_max?: number | null;
  link_external_job_advertisement?: string | null;
  show_partner_name: boolean;
  status: JobAdvertisementStatus;
  use_heyflow: boolean;
  heyflow_flow_id?: string | null;
  heyflow_custom_params?: Record<string, string> | null;
  created_at: string;
  updated_at: string;
}

export interface JobApplication {
  id: string;
  job_advertisement_id: string;
  candidate_id: string;
  company_id: string;
  source: JobApplicationSource;
  source_reference?: string | null;
  status: JobApplicationStatus;
  responses: Record<string, unknown>;
  cv_url?: string | null;
  notes?: string | null;
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

export interface Conversation {
  id: string;
  company_id: string;
  candidate_id: string;
  created_at: string;
  updated_at: string;
}

export interface Candidate {
  id: string;
  company_id: string;
  first_name: string;
  last_name: string;
  gender?: Gender | null;
  email: string;
  phone?: string | null;
  date_of_birth?: string | null;
  earliest_start_date?: string | null;
  salary_expectation?: number | null;
  language?: string | null;
  location_preference?: string | null;
  remote_preference: boolean;
  availability_notes?: string | null;
  cv_file_url?: string | null;
  certificates_file_url?: string | null;
  cover_letter_file_url?: string | null;
  work_references_file_url?: string | null;
  desired_job_title?: string | null;
  source?: CandidateSource | null;
  source_reference?: string | null;
  notes?: string | null;
  partner_notes?: string | null;
  status: CandidateStatus;
  created_at: string;
  updated_at: string;
}

export interface JobSession {
  id: string;
  candidate_id: string;
  recruiter_id: string;
  name: string;
  status: JobSessionStatus;
  shared_to_candidate: boolean;
  candidate_token: string;
  created_at: string;
}

export interface JobSessionAdvertisement {
  id: string;
  job_session_id: string;
  job_advertisement_id: string;
  partner_id: string;
  partner_token: string;
  partner_contact_email?: string | null;
  partner_contact_first_name?: string | null;
  partner_contact_last_name?: string | null;
  candidate_interest?: CandidateInterest | null;
  partner_interest: PartnerInterest;
  shared_to_partner: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobSessionLogMetadata {
  candidate_id?: string;
  partner_id?: string;
  partner_contact_id?: string;
  partner_contact_email?: string;
  partner_contact_first_name?: string;
  partner_contact_last_name?: string;
  job_advertisement_id?: string;
  job_session_advertisement_id?: string;
  session_name?: string;
  candidate_interest?: CandidateInterest;
  partner_interest?: PartnerInterest;
  previous_interest?: CandidateInterest | PartnerInterest | null;
  previous_status?: JobSessionStatus;
  [key: string]: unknown;
}

export interface JobSessionLog {
  id: string;
  job_session_id: string;
  actor_type: ActorType;
  actor_id?: string | null;
  action: string;
  metadata?: JobSessionLogMetadata | null;
  created_at: string;
}

export interface JobSessionLogWithDetails extends JobSessionLog {
  candidate_first_name?: string | null;
  candidate_last_name?: string | null;
  partner_name?: string | null;
  partner_contact_first_name?: string | null;
  partner_contact_last_name?: string | null;
  job_title?: string | null;
}

export interface Company {
  id: string;
  name: string;
  plan: CompanyPlan;
  logo_url?: string | null;
  color_scheme?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  period_ends_at?: string;
  cancel_at_period_end: boolean
  stripe_subscription_status?: StripeSubscriptionStatus;
  stripe_price_id?: string;
  created_at: string;
}

export interface User {
  id: string;
  company_id: string | null;
  role: UserRole;
  language: string;
  first_name: string;
  last_name: string;
  email: string;
  job_title: string;
  created_at: string;
}

export interface Partner {
  id: string;
  company_id: string;
  name: string;
  logo_url?: string | null;
  description?: string | null;
  created_at: string;
}

export interface Contact {
  id: string;
  company_id: string;
  partner_id?: string | null;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  job_title?: string | null;
  created_at: string;
}

export interface CalendarToken {
  id: string;
  user_id: string;
  provider: CalendarProvider;
  provider_account_id: string;
  access_token: string;
  refresh_token: string;
  expires_at: string;
  scope?: string | null;
  is_business_account: boolean;
  created_at: string;
  updated_at: string;
}

export interface AppointmentSession {
  id: string;
  organizer_id: string;
  company_id: string;
  proposer_partner_id: string | null;
  proposer_contact_id: string | null;
  proposer_first_name: string | null;
  proposer_last_name: string | null;
  proposer_email: string | null;
  selector_partner_id: string | null;
  selector_contact_id: string | null;
  selector_first_name: string | null;
  selector_last_name: string | null;
  selector_email: string | null;
  status: AppointmentStatus;
  proposer_token: string | null;
  proposer_token_expires_at: string | null;
  selector_token: string | null;
  selector_token_expires_at: string | null;
  title: string | null;
  description?: string | null;
  session_type: SessionType;
  meeting_type?: MeetingType | null;
  location?: string | null;
  individual_days?: Weekday[] | null;
  start_time?: string | null;
  end_time?: string | null;
  google_event_id?: string | null;
  outlook_event_id?: string | null;
  created_at: string;
}

export interface AppointmentSlot {
  id: string;
  session_id: string;
  start_time: string;
  end_time: string;
  status: AppointmentSlotStatus;
  created_at: string;
  updated_at: string;
}

export interface MailTokenValidation {
  token: string;
  created_at: string;
}

export interface PersonSnapshot {
  first_name: string | null;
  last_name: string | null;
  email: string;
}

export interface SessionStartedMeta {
  proposer: PersonSnapshot;
  selector: PersonSnapshot;
  proposer_partner_id: string;
  proposer_name: string;
  proposer_contact_id: string;
  title?: string;
}

export interface SessionOpenedMeta {
  actor_type: 'participant';
  role: 'proposer' | 'selector';
  source: 'link';
}

export interface SessionStatusChangedMeta {
  actor_type: 'system';
  source: 'system';
  old_status: AppointmentStatus;
  new_status: AppointmentStatus;
}

export interface ProposalsSubmittedMeta {
  proposer: PersonSnapshot;
  proposer_partner_id: string;
  proposer_name: string;
  proposer_contact_id: string;
  proposed_slots: string[];
}

export interface ProposalsReplacedMeta {
  actor_type: 'participant';
  role: 'proposer';
  source: 'link';
  proposer: PersonSnapshot;
  proposer_partner_id: string;
  proposer_name: string;
  proposer_contact_id: string;
  removed_slots: string[];
  added_slots: string[];
}

export interface AppointmentConfirmedMeta {
  selector: PersonSnapshot;
  proposer_partner_id: string;
  proposer_name: string;
  proposer_contact_id: string;
  chosen_slot: string;
}

export interface AppointmentRejectedMeta {
  actor_type: 'participant';
  source: 'link';
  selector: PersonSnapshot;
  proposer_partner_id: string;
  proposer_name: string;
  proposer_contact_id: string;
  reason: string;
}

export interface AppointmentSyncedMeta {
  actor_type: 'system';
  source: 'calendar';
  provider: 'google' | 'outlook';
  result: 'success' | 'failure';
  error?: string;
}

export interface NotificationSentMeta {
  actor_type: 'system';
  source: 'system';
  recipient_role: 'partner' | 'participant';
  channel: 'email';
  template: string;
}

export interface SessionDeletedMeta {
  actor_type: 'user';
  deleted_by: PersonSnapshot;
  session_status: string;
  reason?: 'manual_deletion' | 'automatic_cleanup';
}

export interface ApplicationActivityMeta {
  actor_type: 'user';
  application_id: string;
  candidate_id: string;
  job_advertisement_id: string;
  previous_status?: JobApplicationStatus;
  new_status?: JobApplicationStatus;
  job_session_id?: string;
}

export interface HeatmapCell {
  weekday: string;
  hour: number;
  value: number;
  intensity: number;
}

export interface HeatmapData {
  data: HeatmapCell[];
  maxValue: number;
}

export interface ActivityLog {
  id: string;
  created_at: string;
  user_id: string | null;
  company_id: string;
  action: ActivityAction;
  entity_type: ActivityEntityType;
  entity_id: string;
  meta:
    | SessionStartedMeta
    | SessionOpenedMeta
    | SessionDeletedMeta
    | SessionStatusChangedMeta
    | ProposalsSubmittedMeta
    | ProposalsReplacedMeta
    | AppointmentConfirmedMeta
    | AppointmentRejectedMeta
    | AppointmentSyncedMeta
    | NotificationSentMeta
    | ApplicationActivityMeta;
}

// Database Table Names
export const TABLE_NAMES = {
  COMPANIES: 'companies',
  USERS: 'users',
  PARTNERS: 'partners',
  CONTACTS: 'contacts',
  CALENDAR_TOKENS: 'calendar_tokens',
  APPOINTMENT_SESSIONS: 'appointment_sessions',
  APPOINTMENT_SLOTS: 'appointment_slots',
  MAIL_TOKEN_VALIDATION: 'mail_token_validation',
  ACTIVITY_LOG: 'activity_log',
  ADDON_SUBSCRIPTIONS: 'addon_subscriptions',
  JOB_ADVERTISEMENTS: 'job_advertisements',
  CANDIDATES: 'candidates',
  JOB_SESSIONS: 'job_sessions',
  JOB_SESSION_ADVERTISEMENTS: 'job_session_advertisements',
  JOB_SESSION_LOGS: 'job_session_logs',
  RECRUITING_SETTINGS: 'recruiting_settings',
  JOB_APPLICATIONS: 'job_applications',
  CONVERSATIONS: 'conversations'
} as const;

// Type for table names
export type TableName = typeof TABLE_NAMES[keyof typeof TABLE_NAMES];

// Database entity map
export interface DatabaseTables {
  [TABLE_NAMES.COMPANIES]: Company;
  [TABLE_NAMES.USERS]: User;
  [TABLE_NAMES.PARTNERS]: Partner;
  [TABLE_NAMES.CONTACTS]: Contact;
  [TABLE_NAMES.CALENDAR_TOKENS]: CalendarToken;
  [TABLE_NAMES.APPOINTMENT_SESSIONS]: AppointmentSession;
  [TABLE_NAMES.APPOINTMENT_SLOTS]: AppointmentSlot;
  [TABLE_NAMES.MAIL_TOKEN_VALIDATION]: MailTokenValidation;
  [TABLE_NAMES.ACTIVITY_LOG]: ActivityLog;
  [TABLE_NAMES.ADDON_SUBSCRIPTIONS]: AddonSubscription;
  [TABLE_NAMES.JOB_ADVERTISEMENTS]: JobAdvertisement;
  [TABLE_NAMES.CANDIDATES]: Candidate;
  [TABLE_NAMES.JOB_SESSIONS]: JobSession;
  [TABLE_NAMES.JOB_SESSION_ADVERTISEMENTS]: JobSessionAdvertisement;
  [TABLE_NAMES.JOB_SESSION_LOGS]: JobSessionLog;
  [TABLE_NAMES.RECRUITING_SETTINGS]: RecruitingSettings;
  [TABLE_NAMES.JOB_APPLICATIONS]: JobApplication;
  [TABLE_NAMES.CONVERSATIONS]: Conversation;
}